import { BrandAttribution } from '@/components/layout/BrandAttribution';
import { SHELL_GUTTER_STYLE, SIDEBAR_WIDTH_CLASS } from '@/components/layout/shellGeometry';
import { SIDEBAR_FIT_STYLE } from '@/components/layout/sidebarFit';
import InviteModal from '@/components/team/InviteModal';
import { useAuth } from '@/contexts/AuthContext';
import { usePendingDecisions } from '@/hooks/data/useDecisionFeedData';
import { useMergeReviews } from '@/hooks/data/useMergeReviewsData';
import { useOpenQuestions } from '@/hooks/data/useOpenQuestionsData';
import { useOrderQuestions } from '@/hooks/data/useOrderQuestionsData';
import { useReviewQueuePage } from '@/hooks/data/useReviewQueueData';
import { useTeam } from '@/hooks/data/useTeamData';
import { useVendorList } from '@/hooks/data/useVendorList';
import { vendorQuestionCount } from '@/types/businessProfile/vendorList';
import { cn } from '@/utils';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  ChartBarSquareIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  HomeIcon,
  InboxIcon,
  ShareIcon,
  SignalIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { CheckCircle2, ChevronDown, Plug, Sparkles, UserPlus, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Sizes: a phone's drawer keeps the compact sidebar; from lg the lengths grow with the window's height
// (sidebarFit.ts, Estevan 10-15). A phone browser's 100vh is its height with the address bar hidden, which
// the drawer does not get, so the window-height rule is applied only where there is no such bar.
// ---------------------------------------------------------------------------

const ROW_SIZE = 'h-[30px] text-[13.5px] lg:h-[var(--sb-row)] lg:text-[length:var(--sb-text)]';
const ICON_SIZE = 'h-4 w-4 lg:h-[var(--sb-icon)] lg:w-[var(--sb-icon)]';
// leading-4 AFTER the font size: tailwind-merge (cn) drops a line height that comes before a font size.
const LABEL_SIZE = 'text-[11px] leading-4 lg:text-[length:var(--sb-label)]';
const NAV_SPACING = 'pt-3 pb-2 space-y-2.5 lg:pt-[var(--sb-pad-top)] lg:pb-0.5 lg:space-y-[var(--sb-section-gap)]';
const INVITE_SIZE = 'h-[34px] text-[13.5px] lg:h-[var(--sb-invite)] lg:text-[length:var(--sb-text)]';
// From lg the rows' icons sit on the header's logo line (the shell gutter): the list is inset by the gutter less
// a row's own px-2.5, so an active pill bleeds that far left of the line (shellGeometry.ts, Estevan 10-17).
const CONTENT_INSET = 'px-3 lg:pl-[calc(var(--shell-gutter)_-_0.625rem)]';

// ---------------------------------------------------------------------------
// Navigation config — grouped by JTBD
// ---------------------------------------------------------------------------

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  enabled: boolean;
  badge?: number;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const sections: NavSection[] = [
  {
    label: 'Workspace',
    items: [
      { name: 'Dashboard', href: '/', icon: HomeIcon, enabled: true },
      { name: 'People', href: '/people', icon: UsersIcon, enabled: true },
      { name: 'Connections', href: '/connections', icon: Plug, enabled: true },
      { name: 'What If', href: '/what-if', icon: EyeIcon, enabled: true },
      { name: 'Documents', href: '/documents', icon: DocumentTextIcon, enabled: true },
    ],
  },
  {
    label: 'Review',
    items: [
      { name: 'Inbox', href: '/inbox', icon: InboxIcon, enabled: true },
      { name: 'Entity Decisions', href: '/entity-decisions', icon: CheckCircle2, enabled: true },
    ],
  },
  {
    label: 'Configure',
    items: [
      // XC-BP1 §9.7 / R-VIS: shown to every signed-in user; the page renders its empty state on a 404.
      { name: 'Business Profile', href: '/business-profile', icon: BuildingOfficeIcon, enabled: true },
      { name: 'Rules', href: '/rules', icon: ClipboardDocumentCheckIcon, enabled: true },
      { name: 'Agents', href: '/agents', icon: Sparkles, enabled: true },
      { name: 'Reports', href: '/reports', icon: ChartBarSquareIcon, enabled: true },
    ],
  },
];

const observabilitySection: NavSection = {
  label: 'Observability',
  items: [
    { name: 'Pipeline', href: '/pipeline', icon: SignalIcon, enabled: true },
    { name: 'Action History', href: '/history', icon: ClockIcon, enabled: true },
    { name: 'Graph Explorer', href: '/graph', icon: ShareIcon, enabled: true },
  ],
};

// ---------------------------------------------------------------------------
// Folded sections (XC-TEAM-INVITES mockup screen 1): remembered per person
// ---------------------------------------------------------------------------

export const FOLDED_KEY_PREFIX = 'askwingo_sidebar_folded:';

function readFolded(userId: string | undefined): Set<string> {
  if (!userId) return new Set();
  try {
    const raw = localStorage.getItem(FOLDED_KEY_PREFIX + userId);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeFolded(userId: string | undefined, folded: Set<string>): void {
  if (!userId) return;
  try {
    localStorage.setItem(FOLDED_KEY_PREFIX + userId, JSON.stringify([...folded]));
  } catch {
    /* storage unavailable: sections simply open again next visit */
  }
}

// ---------------------------------------------------------------------------
// Sidebar component
// ---------------------------------------------------------------------------

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const userId = user?.user_id;
  const [folded, setFolded] = useState<Set<string>>(() => readFolded(userId));
  const [inviting, setInviting] = useState(false);
  useEffect(() => setFolded(readFolded(userId)), [userId]);
  const toggleSection = useCallback(
    (label: string) =>
      setFolded((prev) => {
        const next = new Set(prev);
        if (next.has(label)) next.delete(label);
        else next.add(label);
        writeFolded(userId, next);
        return next;
      }),
    [userId],
  );

  // Combined Inbox badge: kinetic IN_REVIEW traces + ER pending decisions +
  // the items Wingo asks a unit question about + the merges Wingo asks him to review.
  // The number a user sees must count DECISIONS THEY OWE, never raw rows
  // (Estevan 2026-08-17: the KPI pending_count showed 525 while the list held
  // far fewer cards — AW-22 groups siblings into one representative whose
  // verdict fans out, so raw rows overstate the ask and scare the user).
  // usePendingDecisions' total IS the deduped representative count
  // (query_pending: total = len(reps)) — same source the Inbox page renders,
  // so the badge and the list can never disagree. Extends AW-51's true-total
  // rule: the TRUE total of an inbox is actions owed. An open question Wingo
  // asks is one action per ITEM (XC-PRICE-GUARD U3b §5: total_items, the same
  // key the Inbox's Questions section renders).
  const reviewQueue = useReviewQueuePage(1, 0);
  const pendingDecisions = usePendingDecisions(1);
  const openQuestions = useOpenQuestions();
  // The order plan's questions (XC-INV2PO answering): one action per card, the key the Inbox reads.
  const orderQuestions = useOrderQuestions();
  // His vendor list's questions (XC-INV2PO DB6): the key the Inbox and "How you order" read.
  const vendorList = useVendorList();
  // Merges Wingo made before he answered (board s1389-1 (b)): one per item, the key the Inbox reads.
  const mergeReviews = useMergeReviews();
  // A Member's invite request waits in an Owner's or Admin's Inbox (XC-TEAM-INVITES T2).
  const team = useTeam();
  const inviteRequests = team.data?.can_manage ? team.data.requests.length : 0;
  const inboxCount =
    (reviewQueue.data?.total ?? 0) +
    (pendingDecisions.data?.total ?? 0) +
    (openQuestions.data?.total_items ?? 0) +
    (orderQuestions.data?.total ?? 0) +
    vendorQuestionCount(vendorList.data) +
    (mergeReviews.data?.total ?? 0) +
    inviteRequests;

  const sectionsWithBadges = useMemo(
    () =>
      sections.map((s) => ({
        ...s,
        items: s.items.map((item) =>
          item.href === '/inbox' && inboxCount > 0
            ? { ...item, badge: inboxCount }
            : item,
        ),
      })),
    [inboxCount],
  );

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const renderNavItem = (item: NavItem) => {
    // Exact match for top-level routes; prefix-match for routes that
    // own a detail page (e.g. /people highlights for /people/:id too).
    const isActive =
      location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href + '/'));

    if (!item.enabled) {
      return (
        <div
          key={item.name}
          data-sidebar-row
          className={cn(
            'group relative flex items-center gap-2.5 px-2.5 font-medium rounded-lg text-gray-400 cursor-not-allowed',
            ROW_SIZE,
          )}
          title="Coming Soon"
        >
          <item.icon className={cn(ICON_SIZE, 'text-gray-400 flex-shrink-0')} />
          <span>{item.name}</span>
          <span className="ml-auto text-[10px] font-medium text-gray-400 bg-white/5 px-1.5 py-0.5 rounded-full">
            Soon
          </span>
        </div>
      );
    }

    return (
      <NavLink
        key={item.name}
        to={item.href}
        onClick={() => onClose()}
        data-sidebar-row
        className={cn('relative flex items-center gap-2.5 px-2.5 font-medium rounded-lg transition-all duration-200', ROW_SIZE)}
      >
        {isActive && (
          <motion.div
            layoutId="sidebar-active"
            className="absolute inset-0 bg-gradient-to-r from-brand-600/20 to-accent-600/20 border border-brand-500/30 rounded-lg"
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
          />
        )}
        <item.icon
          className={cn(
            ICON_SIZE,
            'flex-shrink-0 relative z-10 transition-colors',
            isActive ? 'text-brand-400' : 'text-gray-400 group-hover:text-white',
          )}
        />
        <span
          className={cn(
            'relative z-10 transition-colors',
            isActive ? 'text-white' : 'text-gray-300 hover:text-white',
          )}
        >
          {item.name}
        </span>
        {item.badge !== undefined && item.badge > 0 && (
          <span className="ml-auto relative z-10 min-w-[18px] h-[18px] flex items-center justify-center text-[11px] font-semibold bg-amber-500/90 text-white rounded-full px-1.5">
            {item.badge}
          </span>
        )}
      </NavLink>
    );
  };

  const renderSection = (section: NavSection) => {
    const isFolded = folded.has(section.label);
    const sectionId = `sidebar-section-${section.label.toLowerCase()}`;
    return (
      <div key={section.label} className="space-y-0.5">
        <button
          type="button"
          onClick={() => toggleSection(section.label)}
          aria-expanded={!isFolded}
          aria-controls={sectionId}
          className={cn(
            // from lg its chevron starts where the rows' icons do, on the logo line
            'w-full flex items-center gap-1 px-1 lg:pl-2.5 py-0.5 font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-200',
            LABEL_SIZE,
          )}
        >
          <ChevronDown
            className={cn('h-3 w-3 transition-transform', isFolded && '-rotate-90')}
            aria-hidden="true"
          />
          {section.label}
        </button>
        <div id={sectionId} hidden={isFolded}>
          {section.items.map(renderNavItem)}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar: its lengths grow with the window's height (sidebarFit.ts, Estevan 10-15) */}
      <div
        style={{ ...SIDEBAR_FIT_STYLE, ...SHELL_GUTTER_STYLE }}
        className={cn(
          SIDEBAR_WIDTH_CLASS,
          'bg-white/[0.03] backdrop-blur-xl border-r border-white/[0.06] flex flex-col h-full transition-transform duration-300 ease-in-out',
          'lg:translate-x-0',
          // A phone's drawer is pinned to the top of the window (over the header, with its own close row):
          // left to its static place below the header, its bottom 54 px, Invite people included, fell off-screen.
          'fixed top-0 left-0 lg:relative z-50 lg:z-auto',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* A phone's close button sits in the drawer's top corner, beside the first section's name: a row of its
            own took 60 px, and the drawer must fit a phone's visible height (390 x 664, Safari with its bars) */}
        <button
          onClick={onClose}
          aria-label="Close navigation menu"
          className="lg:hidden absolute top-1.5 right-1.5 z-10 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        {/* Main nav — grouped by JTBD; each section folds (remembered per person) */}
        <nav
          className={cn('flex-1 overflow-y-auto', CONTENT_INSET, NAV_SPACING)}
          aria-label="Main"
        >
          {sectionsWithBadges.map((section) => renderSection(section))}
          {renderSection(observabilitySection)}
        </nav>

        {/* Pinned bottom: Invite people (always in view, for everyone), footer */}
        <div className="border-t border-white/[0.04] pt-2 pb-3 flex flex-col gap-2">
          <div className={CONTENT_INSET}>
            <button
              type="button"
              onClick={() => setInviting(true)}
              className={cn(
                // from lg its padding gives back the 1 px border, so its icon sits on the logo line with the rows' icons
                'w-full flex items-center gap-2.5 px-2.5 lg:pl-[calc(0.625rem_-_1px)] font-medium rounded-lg border border-teal-400/35 bg-teal-600/[0.08] text-teal-200 hover:text-white hover:bg-teal-600/20 transition-colors',
                INVITE_SIZE,
              )}
            >
              <UserPlus className={ICON_SIZE} aria-hidden="true" />
              Invite people
            </button>
          </div>
          <BrandAttribution />
        </div>
        <InviteModal isOpen={inviting} onClose={() => setInviting(false)} />
      </div>
    </>
  );
};

export default Sidebar;
